/**
 * H4 pre-mount + post-mount audit logic (Bundle 2 W2-C).
 *
 * Per spec sections 6.1 (pre-mount 5 checks) + 6.2 (post-mount 4 checks).
 * NO real iframe mount happens here; this module ONLY produces a verdict.
 */
import {
  CheckResult,
  CookieAuditResultPayload,
  PostMountAuditInput,
  PreMountAuditInput,
} from './types';

/** Run all 5 pre-mount checks in order, short-circuiting on first failure
 *  EXCEPT Check 3 (reachability) which has a defer+retry contract: returns
 *  `defer_retry` once if the probe fails, so callers can re-run after a 1s
 *  backoff.
 */
export async function runPreMountChecks(
  input: PreMountAuditInput,
  opts: { retryAttempt?: number } = {},
): Promise<CheckResult[]> {
  const results: CheckResult[] = [];

  // Check 1: browser cookie API.
  const c1 = input.cookieApiAvailable();
  results.push({
    check_number: 1,
    check_name: 'browser_cookie_api_available',
    outcome: c1 ? 'pass' : 'fail',
    detail: c1 ? undefined : 'document.cookie not readable',
  });
  if (!c1) return results;

  // Check 2: Scout origin config.
  const c2 = input.scoutOriginConfigured();
  results.push({
    check_number: 2,
    check_name: 'scout_origin_configured',
    outcome: c2 ? 'pass' : 'fail',
    detail: c2 ? undefined : 'bridge.scout_origin is empty',
  });
  if (!c2) return results;

  // Check 3: Scout origin reachable (defer+retry path).
  const c3 = await input.scoutOriginReachable();
  if (!c3) {
    if ((opts.retryAttempt ?? 0) === 0) {
      results.push({
        check_number: 3,
        check_name: 'scout_origin_reachable',
        outcome: 'defer_retry',
        detail: 'first reachability probe failed; caller should retry once',
      });
      return results;
    }
    results.push({
      check_number: 3,
      check_name: 'scout_origin_reachable',
      outcome: 'fail',
      detail: 'reachability failed after retry',
    });
    return results;
  }
  results.push({
    check_number: 3,
    check_name: 'scout_origin_reachable',
    outcome: 'pass',
  });

  // Check 4: 3rd-party cookie not blocked.
  const c4 = input.cookieApiResponds();
  results.push({
    check_number: 4,
    check_name: 'cookie_api_responds',
    outcome: c4 ? 'pass' : 'fail',
    detail: c4 ? undefined : '3rd-party cookies blocked',
  });
  if (!c4) return results;

  // Check 5: not nested in 3rd-party iframe.
  const c5 = input.notNestedInThirdParty();
  results.push({
    check_number: 5,
    check_name: 'not_nested_in_third_party',
    outcome: c5 ? 'pass' : 'fail',
    detail: c5 ? undefined : 'workspace loaded inside a 3rd-party iframe',
  });
  return results;
}

/** Run the post-mount audit: await the cookie.audit_result ping (with 5s
 *  timeout) and produce the 4 post-mount check results.
 */
export async function runPostMountChecks(
  input: PostMountAuditInput,
  timeoutMs: number = 5_000,
): Promise<{
  ping: CookieAuditResultPayload | 'timeout';
  user_match: boolean;
}> {
  const ping = await input.awaitCookieAuditResult(timeoutMs);
  if (ping === 'timeout') {
    return { ping: 'timeout', user_match: false };
  }
  const user_match =
    ping.user_id_in_session === undefined || ping.user_id_in_session === input.shellSessionUserId;
  return { ping, user_match };
}

/** Helper: check whether the pre-mount sequence concluded with all-pass. */
export function allPreMountChecksPassed(results: ReadonlyArray<CheckResult>): boolean {
  return results.length === 5 && results.every((r) => r.outcome === 'pass');
}

/** Helper: returns the FIRST non-pass check (or null if all pass). */
export function firstFailingPreMountCheck(
  results: ReadonlyArray<CheckResult>,
): CheckResult | null {
  for (const r of results) if (r.outcome !== 'pass') return r;
  return null;
}
